import React, { useState, useEffect } from 'react';
import './Versions.scss';
import WorkplaceTable from 'components/Table/WorkplaceTable';
import BasePagination from 'base-components/BasePagination/BasePagination';
import GridColumnsConfigProvider from 'providers/GridColumnsConfigProvider';
import { GridFilterUrlProvider } from 'providers/GridFilterUrlProvider';
import { MdDelete, MdEdit } from 'react-icons/md';
import BaseButton from 'base-components/BaseButton/BaseButton';
import VersionsEditModal from './parts/VersionsEditModal';
import VersionsDeleteModal from './parts/VersionDeletingModal';
import BaseValidatedDropDownList from 'base-components/BaseValidatedDropDownList/BaseValidatedDropDownList';
import BaseForm from 'base-components/BaseForm/BaseForm';
import { GetVersionsQueryParams, VersionItem, VersionItemAdd, VersionsListResponse } from 'models/api/versions';
import { getVersions, createVersion, updateVersion, deleteVersion, setVersionStatus, setVersionBlock } from 'api/versionsApi';
import useApi from 'hooks/useApi';
import DataTimeText from 'components/Common/DataTime';
import { VersionStatus } from 'models/api/status';
import { setEmergencyMaintenance, completeMaintenance } from 'api/maintenanceApi';
import { defaultPagination, PLATFORMS } from 'utils/consts';
import { useAuth } from 'hooks/useAuth';
import { useSearchParams } from 'react-router-dom';
import { Platform } from 'models/api/platform';

const Versions: React.FC = () => {
    const api = useApi();
    const { hasEmergency, fetchGeneralInfo } = useAuth();
    const [searchParams] = useSearchParams()

    const [tableData, setTableData] = useState<VersionsListResponse>({} as VersionsListResponse);

    const filter = {
        orderColumn: 'status',
        orderDesc: true,
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    
    const fetchVersions = async (filter?: Platform) => {
        setLoading(true);

        setError(null);

        const pageSize = Number(searchParams.get('pageSize'));
        const pageNumber = Number(searchParams.get('pageNum'));

        const params: GetVersionsQueryParams = pageSize || pageNumber ? { pageSize, pageNumber } : defaultPagination;

        params.platformId = filter ?? null;

        try {
            const { data }: any = await getVersions(api, params);
            setTableData({ data: data.records, rowsCount: data.allRecordsCount })
        } catch (err: unknown) {
          setError((err as Error).message || 'Failed to load versions');
        } finally {
          setLoading(false);
        }
    };   

    useEffect(() => {
        fetchVersions()
    }, [searchParams])

    const filterParams = {
        orderColumn: 'platform',
        orderDesc: true,
    }

    const [isCreateModalVisible, setCreateModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const [isDeletingModalVisible, setDeletingModalVisible] = useState(false);
    const [isMaintenanceModalVisible, setMaintenanceModalVisible] = useState(false);

    const handleMaintenance = () => {
        setMaintenanceModalVisible(true)
    }

    const setMaintenance = async () => {
        try {
            if (hasEmergency) {
                await completeMaintenance(api, { platform: Platform.All })
            } else {
                await setEmergencyMaintenance(api)
            }
        } catch (e) {
            console.log('Error while request: ', e)
        } finally {
            await fetchGeneralInfo()
        }
    }

    const [deletedRow, setDeletedRow] = useState({} as VersionItem);
    const handleDeleteRow = (row: VersionItem) => {
        setDeletedRow(row);
        setDeletingModalVisible(true);
    };
    const deleteRow = async () => {
        await deleteVersion(api, deletedRow.id)

        await fetchVersions()
    }

    const setVersionHandler = async (form: VersionItemAdd) => {
        await createVersion(api, form);

        await fetchVersions()
    };

    const setEditableVersionHandler = async (form: VersionItem, shouldUpdateBody?: boolean, shouldUpdateStatus?: boolean) => {
        if (shouldUpdateBody) {
            await updateVersion(api, form);
        }

        if (shouldUpdateStatus) {
            if (form.status === VersionStatus.Blocked) {
                await setVersionBlock(api, form.id);
            } else {
                await setVersionStatus(api, form.id, form.status);
            }
        }

        await fetchVersions()
    }

    const [editableRow, setEditableRow] = useState({} as VersionItem)
    const handleEditModalOpening = (row: VersionItem) => {
        setEditableRow(row)

        setEditModalVisible(true);
    }

    const initColumns= [
        { key: 'name', name: 'Версия' },
        { key: 'platform', name: 'Платформа' },
        { key: 'description', name: 'Примечание' },
        { key: 'status', name: 'Статус' },
        {
            key: 'creationDate',
            name: 'Создан',
            formatter: ({ row }: { row: VersionItem }) => (
                <>
                    <div>
                        <DataTimeText value={row.creationDate} />
                    </div>
                </>
            ),
        },
        {
            key: 'maintenanceEnd',
            name: 'Дата обновления',
            formatter: ({ row }: { row: VersionItem }) => (
                <>
                    <div>
                        <DataTimeText value={row.lastUpdate} />
                    </div>
                </>
            ),
        },
        {
            key: 'actions',
            name: 'Действия',
            cellClass: 'base-data-grid__cell_centered no-change-modal',
            headerCellClass: 'base-data-grid__cell_centered',
            width: 100,
            minWidth: 100,
            draggable: false,
            resizable: false,
            clickDisable: true,
            formatter: ({ row }: { row: VersionItem }) => {
                return (
                    <>
                        <MdEdit
                            size={20}
                            className={`cursor--pointer actions-icon ${row.status === VersionStatus.Deprecated ? 'disabled' : ''}`}
                            onClick={() => handleEditModalOpening(row)}
                        />
                        <MdDelete
                            size={20}
                            className="cursor--pointer actions-icon"
                            onClick={() => handleDeleteRow(row)}
                        />                    
                    </>
                );
            },
        },
    ]

    return (
        <header className="versions">
            <div className="versions__left">
                <h1>Список версий</h1>
                <div className='d-flex justify-content-between align-items-center my-4'>
                    <BaseButton
                        className="sidebar__button"
                        type="submit"
                        color="primary"
                        data-testid="login-button"
                        onClick={() => setCreateModalVisible(true)}
                    >
                        <span>
                            Добавить версию
                        </span>
                    </BaseButton>
                    {
                        hasEmergency !== null && (
                            <BaseButton
                                className="sidebar__button"
                                type="submit"
                                color={hasEmergency ? 'success' : 'danger'}
                                data-testid="login-button"
                                // disabled={hasEmergency}
                                onClick={handleMaintenance}
                            >
                                <span>
                                    { hasEmergency ? 'Снять с ТО' : 'Поставить на ТО' }
                                </span>
                            </BaseButton>
                        )
                    }
                </div>
                <div className="w-25">
                    <BaseForm>
                        <BaseValidatedDropDownList
                            label="Платформа"
                            name="platform"
                            items={PLATFORMS}
                            onValueChange={(filter) => fetchVersions(filter.id as Platform)}
                        />
                    </BaseForm>
                </div>
            </div>
            {
                error ? (<div>Error: {error}</div>) :
                (
                    <div className="versions__right">
                        <GridFilterUrlProvider filterParams={filterParams}>
                            <GridColumnsConfigProvider storagePath={'/'} initColumns={initColumns}>
                                <WorkplaceTable
                                    showSummaryRows
                                    lineHeight={12}
                                    rowClass={() => 'test'}
                                    clickable={false}
                                    tableData={tableData}
                                    filter={filter}
                                    tableLoading={loading}
                                >
                                    {true && (
                                        <div className="base-data-grid__footer">
                                            <BasePagination<VersionItem> tableData={tableData} />
                                        </div>
                                    )}
                                </WorkplaceTable>
                            </GridColumnsConfigProvider>
                        </GridFilterUrlProvider>
                        <VersionsEditModal<VersionItemAdd>
                            isOpen={isCreateModalVisible} 
                            toggle={setCreateModalVisible}
                            handleChange={setVersionHandler}
                        />
                        <VersionsEditModal<VersionItem>
                            isEditing
                            isOpen={isEditModalVisible} 
                            toggle={setEditModalVisible}
                            handleChange={setEditableVersionHandler}
                            editableRow={editableRow}
                        />
                        <VersionsDeleteModal
                            isOpen={isDeletingModalVisible} 
                            toggle={setDeletingModalVisible}
                            handleChange={deleteRow}
                            title='Удаление версии'
                            text='Вы уверены, что хотите удалить версию?'
                        />
                        <VersionsDeleteModal
                            isOpen={isMaintenanceModalVisible} 
                            toggle={setMaintenanceModalVisible}
                            handleChange={setMaintenance}
                            title='Техническое обслуживание'
                            text={hasEmergency ? 'Вы уверены, что хотите снять все версии с ТО?' : 'Вы уверены, что хотите поставить все версии на ТО?'}
                        />                       
                    </div>
                )
            }
        </header>
    );
};

export default Versions;
